import React, { useEffect, useState } from "react";
import { LeftOutlined } from "@ant-design/icons";
import { Spin, Typography } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./teacher-form.module.css";
import CustomButton from "../../../../common/custom-button/custom-button";
import TeacherInformation from "./components/teacher-information/teacher-information";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import TeacherContactInformation from "./components/teacher-contact-information/teacher-contact-information";
import TeacherSchoolInformation from "./components/teacher-school-information/teacher-school-information";
import { addTeacher, fetchTeacherDetails, updateTeacher } from "../../../../services/teachers";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { validateDate } from "../../../../utils/format-date/format-date";

const validationSchema = {
  form1: yup.object().shape({
    title: yup.string().required("please select a title"),
    first_name: yup.string().required("First Name name is required"),
    middle_name: yup.string().notRequired(),
    last_name: yup.string().required("Last name is required"),
    gender: yup.string().required("Gender is required"),
    date_of_birth: yup.string().notRequired(),
    national_id: yup.string().notRequired(),
    full_address: yup.mixed().required("Full address is required"),
    highest_degree: yup.mixed().notRequired(),
  }),
  form2: yup.object().shape({
    phone_number: yup.string().required("Phone number is required"),
    email: yup.string().email("Invalid email").notRequired(),
  }),

  form3: yup.object().shape({
    designation: yup.string().notRequired(),
    subjects_taught: yup
      .array()
      .of(yup.string())
      .min(1, "minimum 1 subject is required")
      .required("subjects are required"),
  }),
};

const defaultValues = {
  form1: {
    title: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    gender: "",
    date_of_birth: "",
    national_id: "",
    full_address: "",
    highest_degree: "",
  },
  form2: {
    phone_number: "",
    email: "",
  },
  form3: {
    designation: "",
    subjects_taught: [],
  },
};

const TEACHERS_FORMS = [
  {
    label: "Teacher Information",
    Component: TeacherInformation,
  },
  {
    label: "Account Information",
    Component: TeacherContactInformation,
  },
  {
    label: "School Information",
    Component: TeacherSchoolInformation,
  },
];

const TeacherForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const teacherId = params.get("uid");
  const [teacherDetails, setTeacherDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  const form1 = useForm({
    resolver: yupResolver(validationSchema.form1),
    defaultValues: defaultValues.form1,
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const form2 = useForm({
    resolver: yupResolver(validationSchema.form2),
    defaultValues: defaultValues.form2,
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const form3 = useForm({
    resolver: yupResolver(validationSchema.form3),
    defaultValues: defaultValues.form3,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const FORM_SET = [form1, form2, form3];

  useEffect(() => {
    if (teacherId) {
      getTeacherData();
    } else {
      setTeacherDetails(null);
    }
  }, [teacherId]);

  const getTeacherData = async () => {
    try {
      setLoading(true);
      const response = await fetchTeacherDetails(teacherId);
      if (response?.data) {
        const data = response?.data?.data;
        setTeacherDetails(data);
        populateFormData(data);
      }
      setLoading(false);
    } catch (error) {
      console.log("error", error);
      setLoading(false);
    }
  };

  const populateFormData = (data) => {
    form1.reset({
      first_name: data?.first_name ?? "",
      middle_name: data?.middle_name ?? "",
      last_name: data?.last_name ?? "",
      gender: data?.gender ?? "",
      date_of_birth: validateDate(data?.date_of_birth),
      national_id: data?.national_id ?? "",
      title: data?.title ?? "",
      full_address: data?.full_address ?? "",
      highest_degree: data?.highest_degree ?? "",
    });

    form2.reset({
      phone_number: data?.phone_number ?? "",
      email: data?.email ?? "",
    });

    form3.reset({
      designation: data?.designation ?? "",
      subjects_taught: [
        ...new Set(data?.subjects_taught?.map((item) => item?.subject_id))
      ] ?? [],
    });
  };

  const handleSubmitForm = async () => {
    try {
      const formData = new FormData();

      const form1Values = form1.getValues();
      const form2Values = form2.getValues();
      const form3Values = form3.getValues();

      // Form 1
      formData.append("first_name", form1Values?.first_name);
      formData.append("middle_name", form1Values?.middle_name);
      formData.append("last_name", form1Values?.last_name);
      formData.append("gender", form1Values?.gender);
      if (form1Values?.date_of_birth) {
        formData.append(
          "date_of_birth",
          dayjs(form1Values.date_of_birth).format("MM-DD-YYYY")
        );
      }
      formData.append("national_id", form1Values?.national_id);
      formData.append("title", form1Values?.title);
      formData.append("full_address", form1Values?.full_address);
      formData.append("highest_degree", form1Values?.highest_degree);

      // Form 2
      formData.append("phone_number", form2Values?.phone_number);
      formData.append("email", form2Values?.email);

      // Form 2
      formData.append("designation", form3Values?.designation);
      formData.append("subjects_taught", form3Values?.subjects_taught);

      setLoading(true);

      if (teacherDetails) await updateTeacher(teacherDetails?.id, formData);
      else await addTeacher(formData);
      toast.success(`"Teacher is ${teacherDetails ? "Updated" : "Added"} successfully!`);
      navigate(-1);
    } catch (err) {
      console.log(err);
      toast.error(err?.data?.message || "failed to added teacher");
    } finally {
      setLoading(false);
    }
  };

  const handleAddTeacher = async () => {
    const results = await Promise.all([
        form1.trigger(),
        form2.trigger(),
        form3.trigger(),
      ]);

      const allValid = results.every(Boolean);
      if (allValid) {
        await handleSubmitForm();
      }
  };

  return (
    <Spin spinning={loading}>
      <div className={styles.leftSection} onClick={() => navigate(-1)}>
        <LeftOutlined />
        <Typography className={styles.navTitle}>
          {teacherId ? "Edit Teacher" : "Add Teacher"}
        </Typography>
      </div>
      <div className={styles.teacherForm}>
        <div>
          {TEACHERS_FORMS?.map((item, index) => {
            const Component = item?.Component;
            return (
              <div className={styles.formComponentWrapper}>
                <div className={styles.formTitle}>{item?.label}</div>
                <Component {...FORM_SET[index]} />
              </div>
            )
          })}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
          <CustomButton
            onClick={() => navigate(-1)}
            title="Cancel"
            variant="outlined"
            style={{
              width: "95px",
              backgroundColor: "transparent",
              color: "#4A4A4A",
              border: "1px solid #888888",
            }}
          />
          <CustomButton
            onClick={handleAddTeacher}
            title={teacherId ? "Update Teacher" : "Add Teacher"}
            type="submit"
            variant="outlined"
          />
        </div>
      </div>
    </Spin>
  );
};

export default TeacherForm;